import { useState } from "react";
import LegalLayout from "../components/LegalLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, ChevronDown, Loader2 } from "lucide-react";
import { apiClient } from "../App";

const FAQ_ITEMS = [
  {
    q: "How do I add my band to Vivo?",
    a: "Go to the sign-up page, choose 'Create a New Band', enter your band's name and pick the ensemble type (British Brass / Concert / Orchestra / Big Band). You'll get a 6-character join code that you can share with members so they can join with one click. New bands start on a 30-day free trial with full functionality.",
  },
  {
    q: "How do my band members join?",
    a: "Once your band is created, copy the join code from Settings → Band Details. Members go to the sign-up page, choose 'Join an Existing Band', enter the code, pick their section, and they're in. You can also add players manually from the Players page — they'll get a welcome email with a temporary password.",
  },
  {
    q: "Can a player be in more than one band?",
    a: "Yes! A single email address can belong to several bands. After joining the first, use the band switcher in the sidebar → 'Join another band' and enter the second band's code. The dashboard 'My Schedule' card shows the next events across every band you belong to.",
  },
  {
    q: "How much does Vivo cost?",
    a: "£5 per month or £50 per year (save £10). Every new band gets a 30-day free trial — no card required up front. Payments are handled by Stripe; we never see your card details. You can cancel anytime from the Subscription page.",
  },
  {
    q: "How do I respond to a rehearsal or concert?",
    a: "On the dashboard or any event page, tap Yes / Maybe / No. You can also reply directly from the event notification email — one click confirms your response, no login needed. Your RSVP automatically syncs to your calendar if you've subscribed (Profile → Calendar Subscription).",
  },
  {
    q: "I forgot my password.",
    a: "Click 'Forgot password?' on the login page. We'll email you a reset link that expires in an hour. If the email doesn't arrive, check your spam folder — and double-check the address you signed up with (it's case-insensitive but must match).",
  },
  {
    q: "How does the committee email address work?",
    a: "Each band gets a unique 'committee.<your-slug>@vivomusicianmanagement.co.uk' address. Any email sent there is automatically forwarded to every band member you've marked as 'Committee' (Settings → Manage Member Roles). Attachments up to ~8MB are preserved. Replies thread naturally back to the original sender.",
  },
  {
    q: "Can I export my data?",
    a: "Yes. Players, Events, Music, Inventory and Finance can all be exported as PDF or Excel from the relevant page's 'Export' button. For a full GDPR data export of your personal account, go to Profile → Data & Privacy → 'Download my data'.",
  },
  {
    q: "How do I delete my account or my band?",
    a: "Personal account: Profile → Data & Privacy → Danger Zone. Whole band: Settings → Delete Band (admin only; type the band name to confirm). Both are immediate and permanent — there's no undo.",
  },
  {
    q: "I'm having a different problem.",
    a: "Use the contact form below — we usually reply within 1 working day. If it's urgent or your band can't access anything at all, please mention that in the subject line.",
  },
];

const FaqItem = ({ q, a, idx }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/10 rounded-lg overflow-hidden" data-testid={`faq-item-${idx}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 hover:bg-white/[0.02] transition-colors"
        data-testid={`faq-toggle-${idx}`}
        aria-expanded={open}
      >
        <span className="font-medium text-foreground">{q}</span>
        <ChevronDown
          className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="px-5 pb-4 text-muted-foreground text-[14px] leading-relaxed" data-testid={`faq-answer-${idx}`}>
          {a}
        </div>
      )}
    </div>
  );
};

const Support = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", website: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const canSubmit =
    form.name.trim().length >= 1 &&
    form.email.trim().length >= 5 &&
    /^.+@.+\..+$/.test(form.email.trim()) &&
    form.subject.trim().length >= 1 &&
    form.message.trim().length >= 10 &&
    !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await apiClient.post("/support/contact", form);
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "", website: "" });
      toast.success("Message sent. We'll be in touch shortly.");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        toast.error(detail || "Too many messages — please wait a few minutes.");
      } else {
        toast.error(detail || "Couldn't send the message. Please try again in a minute.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LegalLayout title="Support" lastUpdated={null}>
      <p className="text-muted-foreground">
        Need a hand? Browse the most common questions below — or scroll down to send us a message directly.
        We usually reply within one working day.
      </p>

      {/* FAQ */}
      <section className="space-y-3 mt-8" data-testid="support-faq">
        <h2 className="font-heading text-2xl mb-2">Frequently asked questions</h2>
        {FAQ_ITEMS.map((item, i) => (
          <FaqItem key={i} idx={i} q={item.q} a={item.a} />
        ))}
      </section>

      {/* Contact form */}
      <section className="mt-12" id="contact" data-testid="support-contact-section">
        <h2 className="font-heading text-2xl mb-2">Still stuck? Send us a message</h2>
        <p className="text-muted-foreground text-sm mb-6">
          Messages go straight to{" "}
          <a className="text-primary" href="mailto:James@support.vivomusicianmanagement.co.uk">
            James@support.vivomusicianmanagement.co.uk
          </a>
          . We'll reply to the email you provide.
        </p>

        {sent ? (
          <div
            className="border border-emerald-500/30 bg-emerald-500/5 rounded-lg p-6 flex items-start gap-4"
            data-testid="support-sent-banner"
          >
            <CheckCircle2 className="w-6 h-6 text-emerald-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-foreground">Thanks — your message is on its way.</p>
              <p className="text-sm text-muted-foreground mt-1">
                We'll get back to you at the address you provided, usually within one working day.
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSent(false)}
                className="mt-3 -ml-3"
                data-testid="support-send-another-btn"
              >
                Send another message
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="support-contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="support-name">Your name</Label>
                <Input
                  id="support-name"
                  data-testid="support-name-input"
                  required
                  maxLength={120}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Jane Cornet"
                  autoComplete="name"
                />
              </div>
              <div>
                <Label htmlFor="support-email">Your email</Label>
                <Input
                  id="support-email"
                  data-testid="support-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@example.com"
                  autoComplete="email"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="support-subject">Subject</Label>
              <Input
                id="support-subject"
                data-testid="support-subject-input"
                required
                maxLength={200}
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="Quick question about…"
              />
            </div>
            <div>
              <Label htmlFor="support-message">Message</Label>
              <Textarea
                id="support-message"
                data-testid="support-message-input"
                required
                rows={7}
                minLength={10}
                maxLength={8000}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Tell us what's going on. Include the band name and steps to reproduce if it's a bug."
              />
              <p className="text-xs text-muted-foreground mt-1">
                {form.message.length}/8000 characters · minimum 10
              </p>
            </div>

            {/* Honeypot — invisible to humans, bots fill it in */}
            <div className="absolute -left-[9999px]" aria-hidden="true">
              <Label htmlFor="support-website">Website</Label>
              <Input
                id="support-website"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
              />
            </div>

            <div className="pt-2 flex items-center gap-3">
              <Button
                type="submit"
                disabled={!canSubmit}
                data-testid="support-submit-btn"
                className="min-w-[140px]"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…
                  </>
                ) : (
                  "Send message"
                )}
              </Button>
              <p className="text-xs text-muted-foreground">
                By sending you agree to our <a href="/privacy" className="text-primary">Privacy Policy</a>.
              </p>
            </div>
          </form>
        )}
      </section>
    </LegalLayout>
  );
};

export default Support;
